import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewQueueRpc, transformSubmission } from '../api';

interface UseReviewQueueOptions {
  status?: 'pending' | 'approved' | 'rejected';
  limit?: number;
  offset?: number;
}

export function useReviewQueue(options: UseReviewQueueOptions = {}) {
  const { status = 'pending', limit = 20, offset = 0 } = options;

  const query = useQuery({
    queryKey: ['admin', 'review-queue', status, limit, offset],
    queryFn: async () => {
      const { data, error } = await reviewQueueRpc.getPendingSubmissions({
        p_status: status,
        p_limit: limit,
        p_offset: offset,
      });

      if (error) throw error;
      if (!data) throw new Error('No data returned');

      return {
        submissions: data.submissions.map(transformSubmission),
        total: data.total,
      };
    },
  });

  return {
    submissions: query.data?.submissions ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useApproveSubmission() {
  const queryClient = useQueryClient();
  const [isApproving, setIsApproving] = useState(false);

  const approve = useCallback(
    async (submissionId: string, isFeatured = false) => {
      setIsApproving(true);
      try {
        const { error } = await reviewQueueRpc.approveSubmission({
          p_submission_id: submissionId,
          p_is_featured: isFeatured,
        });

        if (error) throw error;

        // Invalidate queries
        await queryClient.invalidateQueries({ queryKey: ['admin', 'review-queue'] });
        return { success: true };
      } catch (err) {
        return { success: false, error: err as Error };
      } finally {
        setIsApproving(false);
      }
    },
    [queryClient]
  );

  return { approve, isApproving };
}

export function useRejectSubmission() {
  const queryClient = useQueryClient();
  const [isRejecting, setIsRejecting] = useState(false);

  const reject = useCallback(
    async (submissionId: string, reason: string) => {
      setIsRejecting(true);
      try {
        const { error } = await reviewQueueRpc.rejectSubmission({
          p_submission_id: submissionId,
          p_reason: reason,
        });

        if (error) throw error;

        // Invalidate queries
        await queryClient.invalidateQueries({ queryKey: ['admin', 'review-queue'] });
        return { success: true };
      } catch (err) {
        return { success: false, error: err as Error };
      } finally {
        setIsRejecting(false);
      }
    },
    [queryClient]
  );

  return { reject, isRejecting };
}
